import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import { useState } from "react"
import { createDataItemSigner } from "@permaweb/aoconnect"
import { useAo } from "../ao/ao"
import { burn, readBurnResult } from "../ao/pool"
import { getLinkedMessages } from "../ao/messages-api"
import { retryable } from "../utils/utils"
import Button from "../components/Button"
import { PageWrapper } from "../components/PageWrapper"
import { Stack, TextField, Typography, Paper } from "@mui/material"

async function readBalanceResult(messageId: string) {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, messageId)
  
  const response = messages.find(m => m.tags["Balance"])
  if (!response) throw new Error("Balance response not found yet")
  
  return response.tags["Balance"]
}

export default function SimpleRemove() {
  const [poolId, setPoolId] = useState("")
  const [balance, setBalance] = useState<string | null>(null)
  const [burnAmount, setBurnAmount] = useState("")
  const [messageId, setMessageId] = useState<string | null>(null)
  const [logs, setLogs] = useState<string[]>([])

  const ao = useAo()
  const activeAddr = useActiveAddress()
  const { connect } = useConnection()

  const log = (msg: string) => setLogs((prev) => [...prev, `${msg}`])

  const handleFetch = async () => {
    // Clear logs
    setLogs([])

    if (!poolId) {
      log("Please enter a Pool Process ID")
      return
    }
    if (!activeAddr) {
      log("Please connect wallet")
      return
    }

    log(`Fetching LP balance...`)
    try {
      const signer = createDataItemSigner(window.arweaveWallet)
      
      const msgId = await ao.message({
        process: poolId,
        signer,
        tags: [
          { name: "Action", value: "Balance" },
          { name: "Target", value: activeAddr },
        ],
      })
      
      log(`Balance request sent. MsgID: ${msgId}`)
      log("Waiting for balance response...")
      
      // Poll for result
      const balanceVal = await retryable(readBalanceResult)(msgId)
      
      setBalance(balanceVal)
      setBurnAmount(balanceVal)
      log(`Balance found: ${balanceVal}`)
      
    } catch (e: any) {
      log(`Error fetching balance: ${e.message}`)
    }
  }

  const handleRemove = async () => {
    if (!poolId || !burnAmount) return
    log(`Burning ${burnAmount} from ${poolId}...`)
    try {
      // call burn function directly
      const mid = await burn(BigInt(burnAmount), poolId, ao)
      setMessageId(mid)
      log(`Burn message sent. ID: ${mid}`)
      
      log("Waiting for burn confirmation...")
      const [withdrawals, confirmationId] = await retryable(readBurnResult)(mid)
      
      log(`Burn Confirmed! Confirmation ID: ${confirmationId}`)
      log(`Withdrawals: ${JSON.stringify(withdrawals)}`)

    } catch (e: any) {
      log(`Error burning: ${e.message}`)
    }
  }

  return (
    <PageWrapper>
      <Stack gap={3} maxWidth="md" margin="0 auto" width="100%">
        <Typography variant="h4">Withdraw Liquidity</Typography>
        
        <Paper sx={{ p: 3, background: 'var(--mui-palette-background-default)' }}>
            <Stack gap={2}>
                {!activeAddr && (
                    <Button onClick={connect}>Connect Wallet</Button>
                )}
                
                <TextField 
                    label="Pool Process ID" 
                    value={poolId} 
                    onChange={(e) => setPoolId(e.target.value)} 
                    fullWidth
                />
                
                <Button className="accent" onClick={handleFetch} disabled={!poolId || !activeAddr}>
                    Fetch Balance
                </Button>

                {balance !== null && (
                    <Stack gap={2} sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography>Current Balance: {balance}</Typography>
                        
                        <Button onClick={handleRemove} className="accent">
                            Withdraw Liquidity
                        </Button>
                    </Stack>
                )}

                {logs.length > 0 && (
                    <Paper sx={{ p: 2, fontFamily: 'monospace', maxHeight: 300, overflow: 'auto' }}>
                        {logs.map((l, i) => (
                            <div key={i}>{l}</div>
                        ))}
                    </Paper>
                )}
            </Stack>
        </Paper>
      </Stack>
    </PageWrapper>
  )
}
